import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, RefreshCw, Trash2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "../../components/ui/Button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/Card";
import { Console } from "../../components/ui/Console";
import { Progress } from "../../components/ui/Progress";
import { fetchWithAuth } from "../../hooks/useAuth";
import { useToast } from "../../hooks/useToast";

interface NomenclatorStats {
  counties: number;
  cities: number;
  lockers: number;
  lastSync: string | null;
  lastStatus: string | null;
  progress?: {
    step: string;
    total: number;
    current: number;
    details?: string;
  } | null;
}

// Use relative paths for API endpoints
const API_BASE = "/api";

export function NomenclatorPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [stats, setStats] = useState<NomenclatorStats | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch logs for nomenclator sync with confidence check
  const { data: logsData } = useQuery({
    queryKey: ["nomenclator-logs"],
    queryFn: async () => {
      try {
        // [Confidence Check] Verify API endpoint and source parameter
        const response = await fetchWithAuth(
          `${API_BASE}/admin/monitoring/logs?source=syncNomenclator&limit=1000`
        );
        if (!response.ok) {
          console.error("Logs API error:", await response.text());
          throw new Error("Failed to fetch logs");
        }
        const data = await response.json();

        // [Confidence Check] Validate response structure
        if (!Array.isArray(data?.logs)) {
          console.error("Invalid logs response:", data);
          throw new Error("Invalid logs response format");
        }

        return data;
      } catch (error) {
        console.error("[Confidence Check] Error fetching logs:", error);
        return { logs: [] }; // Fallback to empty logs
      }
    },
    refetchInterval: stats?.lastStatus === "IN_PROGRESS" ? 200 : 2000,
    refetchIntervalInBackground: true,
  });

  // Filter and format logs with confidence checks
  const consoleLogs = useMemo(() => {
    if (!logsData?.logs) return [];
    try {
      return logsData.logs
        .filter(
          (log: {
            details?: { source?: string; details?: { source?: string } };
          }) => {
            // [Confidence Check] Validate log structure
            if (!log || typeof log !== "object") return false;
            // Check both possible source locations
            return (
              log.details?.source === "syncNomenclator" ||
              log.details?.details?.source === "syncNomenclator"
            );
          }
        )
        .map(
          (log: {
            timestamp: string;
            level: string;
            message: string;
            details?: any;
          }) => {
            // [Confidence Check] Validate required fields
            if (!log.timestamp || !log.level || !log.message) {
              console.error("Invalid log entry:", log);
              return null;
            }
            return {
              timestamp: log.timestamp,
              level: log.level,
              message: log.message,
              details: log.details,
            };
          }
        )
        .filter(Boolean) // Remove invalid entries
        .reverse()
        .slice(0, 100); // Keep last 100 logs
    } catch (error) {
      console.error("[Confidence Check] Error processing logs:", error);
      return [];
    }
  }, [logsData?.logs]);

  // Add React Query for real-time updates with confidence checks
  const { error: queryError } = useQuery({
    gcTime: 0,
    queryKey: ["nomenclator-stats"],
    queryFn: async () => {
      try {
        // [Confidence Check] Verify API endpoint
        const response = await fetchWithAuth(
          `${API_BASE}/admin/settings/nomenclator/stats`
        );
        if (!response.ok) {
          const errorText = await response.text();
          console.error("Stats API error:", errorText);
          throw new Error("Failed to fetch stats");
        }

        const data = await response.json();

        // [Confidence Check] Validate response structure
        if (!data?.data || typeof data.data !== "object") {
          console.error("Invalid stats response:", data);
          throw new Error("Invalid stats response format");
        }

        // [Confidence Check] Validate required fields
        const requiredFields = ["counties", "cities", "lockers", "lastStatus"];
        for (const field of requiredFields) {
          if (!(field in data.data)) {
            throw new Error(`Missing required field: ${field}`);
          }
        }

        setStats(data.data);
        return data.data as NomenclatorStats;
      } catch (error) {
        console.error("[Confidence Check] Error fetching stats:", error);
        throw error;
      }
    },
    refetchInterval: (query) =>
      query.state.data?.lastStatus === "IN_PROGRESS" ? 100 : 5000,
    refetchIntervalInBackground: true,
    retry: 0,
  });

  useEffect(() => {
    if (queryError) {
      toast({
        variant: "destructive",
        title: "Error",
        description:
          queryError instanceof Error
            ? queryError.message
            : "Failed to fetch stats",
      });
    }
  }, [queryError, toast]);

  const pollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number>(0);
  const MAX_POLL_DURATION = 600000; // 10 minutes
  const POLL_INTERVAL = 200; // 200ms

  const fetchStats = async () => {
    try {
      // [Confidence Check] Verify API endpoint
      const response = await fetchWithAuth(
        `${API_BASE}/admin/settings/nomenclator/stats`
      );
      if (!response.ok) {
        const errorText = await response.text();
        console.error("Stats API error:", errorText);
        throw new Error("Failed to fetch stats");
      }

      const data = await response.json();

      // [Confidence Check] Validate response
      if (!data?.data || typeof data.data !== "object") {
        throw new Error("Invalid stats response format");
      }

      setStats(data.data);

      if (data.data.lastStatus === "IN_PROGRESS") {
        if (data.data.progress) {
          schedulePoll();
        } else if (Date.now() - startTimeRef.current > MAX_POLL_DURATION) {
          stopPolling();
          setIsLoading(false);
          toast({
            variant: "destructive",
            title: "Warning",
            description:
              "Sync is taking longer than expected. Please check the logs.",
          });
        } else {
          schedulePoll();
        }
      } else {
        stopPolling();
        setIsLoading(false);

        if (data.data.lastStatus === "SUCCESS") {
          toast({
            title: "Success",
            description: `Sync completed: ${data.data.cities} cities in ${data.data.counties} counties and ${data.data.lockers} lockers`,
          });
        } else if (data.data.lastStatus === "FAILED") {
          toast({
            variant: "destructive",
            title: "Error",
            description: data.data.error || "Sync failed",
          });
        }
      }
    } catch (error) {
      console.error("[Confidence Check] Error in fetchStats:", error);
      stopPolling();
      setIsLoading(false);
      toast({
        variant: "destructive",
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "Failed to fetch nomenclator stats",
      });
    }
  };

  const schedulePoll = () => {
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
    }
    pollTimeoutRef.current = setTimeout(fetchStats, POLL_INTERVAL);
  };

  const stopPolling = () => {
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  };

  const handleSync = async () => {
    setIsLoading(true);
    stopPolling();
    startTimeRef.current = Date.now();

    try {
      // [Confidence Check] Verify API endpoint
      const response = await fetchWithAuth(
        `${API_BASE}/admin/settings/nomenclator/sync`,
        {
          method: "POST",
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Sync API error:", errorText);
        throw new Error("Failed to start sync");
      }

      // [Confidence Check] Validate response
      const data = await response.json();
      if (!data?.status || data.status !== "success") {
        throw new Error("Invalid sync response");
      }

      await fetchStats();
      queryClient.invalidateQueries({ queryKey: ["nomenclator-stats"] });

      toast({
        title: "Success",
        description: "Nomenclator sync started",
      });
    } catch (error) {
      console.error("[Confidence Check] Error in handleSync:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description:
          error instanceof Error ? error.message : "Failed to sync nomenclator",
      });
      setIsLoading(false);
      stopPolling();
    }
  };

  const handleReset = async () => {
    try {
      // [Confidence Check] Verify API endpoint
      const response = await fetchWithAuth(
        `${API_BASE}/admin/settings/nomenclator/reset`,
        {
          method: "POST",
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Reset API error:", errorText);
        throw new Error("Reset failed");
      }

      // [Confidence Check] Validate response
      const data = await response.json();
      if (!data?.status || data.status !== "success") {
        throw new Error("Invalid reset response");
      }

      toast({
        title: "Success",
        description: "Sync reset successfully",
      });

      await fetchStats();
    } catch (error) {
      console.error("[Confidence Check] Error in handleReset:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description:
          error instanceof Error ? error.message : "Failed to reset sync",
      });
    }
  };

  const handleRefreshCache = async () => {
    try {
      // [Confidence Check] Verify API endpoint
      const response = await fetchWithAuth(
        `${API_BASE}/nomenclator/refresh-cache`,
        {
          method: "POST",
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Cache refresh API error:", errorText);
        throw new Error("Cache refresh failed");
      }

      // [Confidence Check] Validate response
      const data = await response.json();
      if (!data?.status || data.status !== "success") {
        throw new Error("Invalid cache refresh response");
      }

      toast({
        title: "Success",
        description: "Nomenclator cache refreshed successfully",
      });

      await fetchStats();
      queryClient.invalidateQueries({ queryKey: ["nomenclator-stats"] });
    } catch (error) {
      console.error("[Confidence Check] Error in handleRefreshCache:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "Failed to refresh nomenclator cache",
      });
    }
  };

  useEffect(() => {
    fetchStats();
    return () => stopPolling();
  }, []);

  const getProgressPercentage = () => {
    if (!stats?.progress) return 0;
    if (stats.progress.total === 0) return 0;
    return Math.round((stats.progress.current / stats.progress.total) * 100);
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold tracking-tight">
          Nomenclator Settings
        </h2>
        <div className="flex gap-2">
          <Button
            onClick={handleRefreshCache}
            variant="outline"
            className="text-blue-600 hover:text-blue-700"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Refresh Cache
          </Button>
          {stats?.lastStatus === "IN_PROGRESS" && (
            <Button
              onClick={handleReset}
              variant="outline"
              className="text-red-600 hover:text-red-700"
            >
              Reset Sync
            </Button>
          )}
          <Button
            onClick={handleSync}
            disabled={
              isLoading ||
              (stats?.lastStatus === "IN_PROGRESS" && stats?.progress !== null)
            }
          >
            {stats?.lastStatus === "IN_PROGRESS" && stats?.progress ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Syncing...
              </>
            ) : isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Starting...
              </>
            ) : (
              <>
                <RefreshCw className="mr-2 h-4 w-4" />
                Sync Now
              </>
            )}
          </Button>
        </div>
      </div>

      {stats?.lastStatus === "IN_PROGRESS" && stats.progress && (
        <Card className="mb-4">
          <CardContent className="pt-6">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>{stats.progress.step}</span>
                <span>{getProgressPercentage()}%</span>
              </div>
              <Progress value={getProgressPercentage()} />
              {stats.progress.details && (
                <p className="text-sm text-gray-500 mt-2">
                  {stats.progress.details}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Console */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Sync Logs</CardTitle>
        </CardHeader>
        <CardContent>
          <Console
            logs={consoleLogs}
            maxHeight="500px"
            className="border border-gray-800"
          />
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Counties</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.counties || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Cities</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.cities || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Lockers</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.lockers || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Last Sync</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-sm">
              {stats?.lastSync
                ? new Date(stats.lastSync).toLocaleString()
                : "Never"}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div
              className={`text-sm font-medium ${
                stats?.lastStatus === "SUCCESS"
                  ? "text-green-600"
                  : stats?.lastStatus === "FAILED"
                  ? "text-red-600"
                  : stats?.lastStatus === "IN_PROGRESS"
                  ? "text-blue-600"
                  : "text-yellow-600"
              }`}
            >
              {stats?.lastStatus || "Unknown"}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
